import type * as XLSX from 'xlsx';
import {
  CreatePartnerContactRequestSchema,
  partnerContactImportKey,
  type CreatePartnerContactRequest,
  type PartnerContact
} from '@bliks-io/contracts';
import {
  buildHeaderToFieldMap,
  buildRows,
  readDataSheetRows,
  readWorkbook,
  type ParsedRow
} from '@/lib/excel';
import { markDuplicateRows, type DuplicateCheck } from '@/lib/draft-rows/markDuplicateRows';

export type ParsedContactRow = ParsedRow<CreatePartnerContactRequest>;

/** What an import would do, row by row, before anything is sent. */
export interface ContactImportPreview {
  /** Rows that will be sent: valid and not named twice in the sheet. */
  rows: ParsedContactRow[];
  /** Rows the rep has to fix in the sheet, with their reasons. */
  invalid: ParsedContactRow[];
  created: number;
  updated: number;
}

// The same identity the backend matches on, so a row flagged here is one the
// import would otherwise collapse with another.
const SAME_PERSON: DuplicateCheck<CreatePartnerContactRequest> = {
  field: 'email',
  keyOf: partnerContactImportKey
};

const text = (value: string | undefined) => value?.trim() ?? '';

export function parsePartnerContactsWorkbook(workbook: XLSX.WorkBook): ParsedContactRow[] {
  const headerToFieldMap = buildHeaderToFieldMap('partnerContacts');
  const rows = buildRows(readDataSheetRows(workbook, headerToFieldMap), headerToFieldMap, {
    schema: CreatePartnerContactRequestSchema,
    toData: (normalized) => ({
      firstName: text(normalized.firstName),
      lastName: text(normalized.lastName),
      company: text(normalized.company),
      email: text(normalized.email),
      phone: text(normalized.phone),
      note: text(normalized.note)
    }),
    isEmpty: (data) => Object.values(data).every((value) => !value)
  });
  return markDuplicateRows(rows, [SAME_PERSON]).rows;
}

export async function parsePartnerContactsFromExcel(file: File): Promise<ParsedContactRow[]> {
  return parsePartnerContactsWorkbook(await readWorkbook(file));
}

/** Splits parsed rows into what will be imported and what needs fixing, counting updates. */
export function previewContactImport(
  rows: ParsedContactRow[],
  existing: PartnerContact[]
): ContactImportPreview {
  const existingKeys = new Set(existing.map(partnerContactImportKey));
  const valid = rows.filter((row) => row.isValid);
  const updated = valid.filter((row) => existingKeys.has(partnerContactImportKey(row.data))).length;
  return {
    rows: valid,
    invalid: rows.filter((row) => !row.isValid),
    created: valid.length - updated,
    updated
  };
}
